import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTaskData } from '../../hooks/useTaskData';
import { useTaskStyling } from '../../hooks/useTaskStyling';
import { useTaskForm } from '../../hooks/useTaskForm';
import TaskButton from './TaskButton';
import TaskHeader from './TaskHeader';
import TaskDescription from './TaskDescription';
import TaskStatusBadge from './TaskStatusBadge';
import TaskForm from './TaskForm';
import { ACTIONS } from '../../constants/actionTables';
import styles from './styles.module.css';
import { Dialog, DialogContent, DialogTitle, IconButton, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ModalButton, ModalDialogActions } from '../ModalComponents';

export default function ModalMaintenanceTask({
  task,
  type,
  fetchdata,
  view,
  machineGroupName,
  machineGroupImg,
}) {
  const [open, setOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [customFieldsSubmitTrigger, setCustomFieldsSubmitTrigger] = useState(0);
  const { t } = useTranslation();

  // Custom hooks
  const { taskData, descHtml, isCompletedOnTime } = useTaskData(
    task,
    type,
    machineGroupName,
    machineGroupImg
  );
  const { backgroundTask, borderColorTask } = useTaskStyling(taskData);

  // Modal handlers
  const handleOpen = () => {
    setOpen(true);
    setScrollPosition(window.pageYOffset);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  // Form handling
  const {
    form,
    errorResponse,
    selectedFile,
    handleChange,
    handleFileChange,
    handleSubmit: originalHandleSubmit,
    resetForm,
  } = useTaskForm(taskData, fetchdata, handleClose, t);

  const handleSubmit = async () => {
    // Trigger custom fields save
    setCustomFieldsSubmitTrigger(prev => prev + 1);

    // Small delay to allow custom fields to save
    await new Promise(resolve => setTimeout(resolve, 100));

    // Then submit main form
    await originalHandleSubmit();
  };

  // Restore scroll position when modal closes
  useEffect(() => {
    if (!open) {
      window.scrollTo(0, scrollPosition);
    }
  }, [open, scrollPosition]);

  const isTaskCompleted = taskData?.status === 'completed' || taskData?.status === 'incompleted';
  const isTaskEditable = type === ACTIONS.CALENDAR && isTaskCompleted;

  return (
    <div>
      <TaskButton
        taskData={taskData}
        type={type}
        view={view}
        backgroundTask={backgroundTask}
        borderColorTask={borderColorTask}
        onOpen={handleOpen}
      />

      {taskData && (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle sx={{ m: 0, p: 2 }}>
            {taskData?.maintenanceName === 'Report a repair'
              ? t('modals.reportRepair')
              : taskData?.maintenanceName || t('modals.maintenanceTask')}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers sx={{ p: 3 }}>
            <TaskHeader taskData={taskData} />
            <Divider />
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px' }}
            >
              <div className={styles.content}>
                <TaskDescription taskData={taskData} descHtml={descHtml} />

                {isTaskEditable ? (
                  <TaskStatusBadge taskData={taskData} isCompletedOnTime={isCompletedOnTime} />
                ) : (
                  <TaskForm
                    form={form}
                    errorResponse={errorResponse}
                    selectedFile={selectedFile}
                    taskData={taskData}
                    handleChange={handleChange}
                    handleFileChange={handleFileChange}
                    submitTrigger={customFieldsSubmitTrigger}
                  />
                )}
              </div>
            </div>
          </DialogContent>

          {/* Only show DialogActions if task is not completed (form is shown) */}
          {!isTaskEditable && (
            <ModalDialogActions>
              <ModalButton variant="outlined" color="primary" onClick={handleClose}>
                {t('modals.close')}
              </ModalButton>
              {taskData?.status !== 'completed' && taskData?.status !== 'incompleted' && (
                <ModalButton variant="contained" color="primary" onClick={handleSubmit}>
                  {t('modals.done')}
                </ModalButton>
              )}
            </ModalDialogActions>
          )}
        </Dialog>
      )}
    </div>
  );
}
